import { useState } from "react";
import * as yup from 'yup';

const useHandleForm = (data, createCallback, modifyCallback, dataTypes, invalidDataCallback, openSelectorValidationError) => {

  const isCreate = !Boolean(data);

  const createInitialData = () => {

    const getInitialFieldData = (fieldName) => {
      if (fieldName === "active") {
        return isCreate ? "Y" : data[fieldName];
      }

      if (fieldName === "selectors") {
        return isCreate ? "" : data[fieldName].join(",");
      }

      if (fieldName === "questions") {
        return isCreate ? [] : (data[fieldName] || []);
      }

      return isCreate ? "" : data[fieldName];
    }

    const fieldNames = [
      "code",
      "name",
      "active",
      "sql",
      "rol",
      "group",
      "sequence",
      "selectors",
      "sqlContext",
      "comment",
      "questions",
    ];

    return fieldNames.reduce((initialData, fieldName) => {
      return ({
        ...initialData,
        [fieldName]: getInitialFieldData(fieldName)
      })
    }, {});
  }

  const [formData, setFormData] = useState(createInitialData());

  const dataIsValid = async () => {

    const codeRegex = /^[A-Z]+[A-Z0-9]*$/;

    const schema = yup.object().shape({
      code: yup.string().matches(codeRegex).max(20).required(),
      name: yup.string().max(254).required(),
      active: yup.string().required(),
      sql: yup.string().required(),
      rol: yup.string().required(),
      group: yup.string().max(100).required(),
      sequence: yup.string().max(3).required(),
      selectors: yup.string().required(),
      sqlContext: yup.string(),
      comment: yup.string(),
      questions: yup.array().of(
        yup.object().shape({
          order: yup.number().required(),
          question: yup.string().required(),
        })
      ).optional(),
    });

    return await schema.isValid(formData).then((valid) => valid);
  }

  const selectorsAreValid = () => {
    const selectors = formData.selectors.split(",");
    const validSelectors = dataTypes.map((item) => item.key);

    return selectors.every((selector) => validSelectors.includes(selector));
  }

  const submit = async () => {
    if (await dataIsValid()) {
      if (selectorsAreValid()) {
        const formDataToSend = { ...formData };

        formDataToSend.sequence = parseInt(formDataToSend.sequence);
        formDataToSend.selectors = formDataToSend.selectors.split(",");

        if (formDataToSend.questions) {
          formDataToSend.questions = formDataToSend.questions.map(q => ({
            ...q,
            order: parseInt(q.order) || 0
          }));
        }

        isCreate ? createCallback(formDataToSend) : modifyCallback(formDataToSend);
        return true;
      }
      openSelectorValidationError();
      return false;
    } else {
      invalidDataCallback();
      return false;
    }
  }

  const formatValue = (value, fieldName) => {
    if (fieldName === "code") {
      return value.length > 0 ? value.replace(/\s/g, "").toUpperCase() : "";
    }

    if (fieldName === "sequence") {
      return value.length > 0 ? value.replace(/\s/g, "") : "";
    }

    if (fieldName === "selectors") {
      if (value.length === 0) return "";
      if (value.charAt(0) === ",") {
        return value.substring(1);
      }
    }

    return value;
  }

  const setFormValue = (value, fieldName) => {
    setFormData({
      ...formData,
      [fieldName]: formatValue(value, fieldName)
    })
  }

  return { isCreate, formData, setFormValue, submit };
}

export default useHandleForm;
